import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import DataProtectionSoftwareService from "src/modules/data-protection/apis/DataProtectionSoftwareService";

import {
    buildApprovalRequirementActionPayload,
    getApprovalRequirementActionErrorKey,
    invalidateApprovalRelatedQueries,
    isRejectionReasonValid,
} from "../helpers";

const useApprovalRequirementAction = ({ softwareId, t, onSuccess } = {}) => {
    const queryClient = useQueryClient();

    const refetchApprovalQueries = () => {
        invalidateApprovalRelatedQueries(queryClient, softwareId);
    };

    const mutation = useMutation({
        mutationFn: async ({ requirementId, payload }) => {
            const service = DataProtectionSoftwareService.getInstance();

            return await service.submitApprovalRequirementAction(
                softwareId,
                requirementId,
                payload
            );
        },
        onSuccess: (_data, variables) => {
            const isReject = variables?.payload?.action === "reject";

            toast(
                t(
                    isReject
                        ? "The approval has been rejected."
                        : "The compliance review has been approved successfully."
                ),
                {
                    type: "success",
                }
            );
            refetchApprovalQueries();

            if (typeof onSuccess === "function") {
                onSuccess();
            }
        },
        onError: (error) => {
            if (process.env.NODE_ENV === "development") {
                console.error(error);
            }

            toast(t(getApprovalRequirementActionErrorKey(error)), {
                type: "error",
            });
        },
    });

    const submit = ({ requirementId, action, rejectionReason } = {}) => {
        if (!softwareId || !requirementId || !action) {
            return;
        }

        if (action === "reject" && !isRejectionReasonValid(rejectionReason)) {
            return;
        }

        const payload = buildApprovalRequirementActionPayload({
            action,
            rejectionReason,
        });

        if (!payload) {
            return;
        }

        mutation.mutate({ requirementId, payload });
    };

    return {
        submit,
        isSubmitting: mutation.isLoading,
    };
};

export default useApprovalRequirementAction;
